/**
 * Canonical Inbox conversation deep-link contract.
 * Shared by Contacts, Reports, and Inbox URL hydration.
 */
export function inboxConversationDeepLinkPath(conversationId: number): string {
  return `/inbox?conversation=${conversationId}`;
}

export function parseInboxConversationDeepLinkParam(
  raw: string | null | undefined,
): number | null {
  if (raw == null || raw === "") {
    return null;
  }
  const conversationId = Number(raw);
  if (!Number.isInteger(conversationId) || conversationId <= 0) {
    return null;
  }
  return conversationId;
}

export function inboxConversationDeepLinkKey(
  organizationId: number,
  conversationId: number,
): string {
  return `${organizationId}:${conversationId}`;
}

export type InboxConversationDeepLinkDecision =
  | { action: "clear" }
  | { action: "invalid" }
  | { action: "already_selected"; conversationId: number }
  | { action: "skip_failed"; conversationId: number }
  | { action: "skip_in_flight"; conversationId: number }
  | { action: "open"; conversationId: number; key: string };

/**
 * Decide whether Inbox should open a conversation from `?conversation=`.
 * The param is a one-shot instruction: every decision except `clear` and the skips
 * is followed by removing it (`shouldConsumeInboxConversationDeepLink`), so a later
 * manual selection is never overridden by the stale URL. Skips cover Strict Mode
 * effect re-entry while the first open is in flight.
 */
export function decideInboxConversationDeepLink(input: {
  rawParam: string | null;
  organizationId: number;
  selectedConversationId: number | null;
  failedKey: string | null;
  inFlightKey: string | null;
}): InboxConversationDeepLinkDecision {
  if (input.rawParam == null || input.rawParam === "") {
    return { action: "clear" };
  }
  const conversationId = parseInboxConversationDeepLinkParam(input.rawParam);
  if (conversationId == null) {
    return { action: "invalid" };
  }
  if (input.selectedConversationId === conversationId) {
    return { action: "already_selected", conversationId };
  }
  const key = inboxConversationDeepLinkKey(input.organizationId, conversationId);
  if (input.failedKey === key) {
    return { action: "skip_failed", conversationId };
  }
  if (input.inFlightKey === key) {
    return { action: "skip_in_flight", conversationId };
  }
  return { action: "open", conversationId, key };
}

export function shouldConsumeInboxConversationDeepLink(
  decision: InboxConversationDeepLinkDecision,
): boolean {
  switch (decision.action) {
    case "invalid":
    case "already_selected":
    case "open":
      return true;
    case "clear":
    case "skip_failed":
    case "skip_in_flight":
      return false;
    default: {
      const _exhaustive: never = decision;
      return _exhaustive;
    }
  }
}

/** Copy of `params` without `conversation`; unrelated params are kept in order. */
export function withoutInboxConversationDeepLink(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete("conversation");
  return next;
}

/**
 * After an async deep-link open resolves, re-select the conversation only when the
 * selection is still that conversation or was cleared by a reset (Strict Mode
 * re-entry, scope change). A selection the user made in the meantime wins.
 */
export function shouldReassertDeepLinkedSelection(input: {
  requestOrganizationId: number;
  currentOrganizationId: number;
  currentSelectedConversationId: number | null;
  conversationId: number;
}): boolean {
  if (input.currentOrganizationId !== input.requestOrganizationId) return false;
  return (
    input.currentSelectedConversationId == null ||
    input.currentSelectedConversationId === input.conversationId
  );
}
